import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileLock2, Loader2, Lock, LockOpen, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getApiUrl } from "@/lib/config";

/**
 * Admin > Acesso Admin: is personal data encrypted on this environment (FILLSIGN_DATA_KEY), and are the stored files?
 * Shows the state the API really runs with, so the claims "CPF, telefone, IP e assinatura com uma chave a mais" and
 * "os arquivos ficam cifrados no armazenamento" can be checked before they are made.
 */
interface EncryptionStatus {
  enabled: boolean;
  keyVariable: string;
  pendingPlaintext: number;
  columns: { table: string; column: string; plaintext: number; encrypted: number }[];
  files?: { encryptNewFiles: boolean; switchVariable: string; switchedOff: boolean };
}

interface FilesPage {
  checked: number;
  encrypted: number;
  plaintext: number;
  converted: number;
  failed: number;
  next: string | null;
  errors: string[];
}

interface FilesTotals {
  checked: number;
  encrypted: number;
  plaintext: number;
  converted: number;
  failed: number;
  done: boolean;
}

const KEY = ["/api/admin/security/encryption"];
const nf = new Intl.NumberFormat("pt-BR");
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}` });

export function EncryptionStatusCard() {
  const { toast } = useToast();
  const { data, isLoading, isError } = useQuery<EncryptionStatus>({
    queryKey: KEY,
    queryFn: async () => (await apiRequest("GET", "/api/admin/security/encryption")).json(),
  });
  const [busy, setBusy] = useState(false);
  const [filesBusy, setFilesBusy] = useState<"scan" | "convert" | null>(null);
  const [scan, setScan] = useState<FilesTotals | null>(null);
  const [converted, setConverted] = useState<FilesTotals | null>(null);

  // Each call encrypts a bounded batch; repeat until nothing is left (the endpoint is idempotent).
  const encryptOld = async () => {
    setBusy(true);
    try {
      for (let i = 0; i < 40; i++) {
        const res = await fetch(getApiUrl("/api/admin/security/encryption/encrypt-legacy"), { method: "POST", headers: authHeaders() });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.message || "Não deu para criptografar agora.");
        if (!body.remaining) break;
      }
      await queryClient.invalidateQueries({ queryKey: KEY });
      toast({ title: "Dados antigos criptografados" });
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : "Erro", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  // Stored files: one page of objects per call, following the cursor until the API says there is nothing left.
  const walkFiles = async (convert: boolean, onPage: (t: FilesTotals) => void): Promise<FilesTotals> => {
    const totals: FilesTotals = { checked: 0, encrypted: 0, plaintext: 0, converted: 0, failed: 0, done: false };
    let after: string | null = null;
    for (let i = 0; i < 5000; i++) {
      const qs = new URLSearchParams({ convert: String(convert) });
      if (after) qs.set("after", after);
      const res = await fetch(getApiUrl(`/api/admin/security/encryption/files?${qs}`), { method: "POST", headers: authHeaders() });
      const body = (await res.json().catch(() => ({}))) as Partial<FilesPage> & { message?: string };
      if (!res.ok) throw new Error(body?.message || "Não deu para verificar os arquivos agora.");
      totals.checked += body.checked ?? 0;
      totals.encrypted += body.encrypted ?? 0;
      totals.plaintext += body.plaintext ?? 0;
      totals.converted += body.converted ?? 0;
      totals.failed += body.failed ?? 0;
      after = body.next ?? null;
      totals.done = !after;
      onPage({ ...totals });
      if (!after) break;
    }
    return totals;
  };

  const scanFiles = async () => {
    setFilesBusy("scan");
    try {
      await walkFiles(false, setScan);
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : "Erro", variant: "destructive" });
    } finally {
      setFilesBusy(null);
    }
  };

  const encryptFiles = async () => {
    setFilesBusy("convert");
    setConverted(null);
    try {
      const t = await walkFiles(true, setConverted);
      toast({ title: t.failed ? `${nf.format(t.converted)} arquivos criptografados, ${nf.format(t.failed)} com erro` : `${nf.format(t.converted)} arquivos criptografados` });
      setFilesBusy("scan");
      await walkFiles(false, setScan); // show the state after the run
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : "Erro", variant: "destructive" });
    } finally {
      setFilesBusy(null);
    }
  };

  const encrypted = data?.columns.reduce((n, c) => n + c.encrypted, 0) ?? 0;
  const files = data?.files;

  return (
    <Card className="mb-4" data-testid="encryption-status">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Privacidade dos dados</CardTitle>
        <CardDescription>
          Arquivos dos clientes e dados pessoais (CPF, telefone, IP e imagem da assinatura) gravados com AES-256-GCM, com uma chave só deste ambiente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {isLoading && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Verificando…</div>}
        {isError && <p className="text-muted-foreground">Não deu para ler o estado da criptografia.</p>}
        {data && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">Criptografia de dados pessoais:</span>
              {data.enabled
                ? <Badge className="bg-green-600 hover:bg-green-600" data-testid="encryption-on"><Lock className="mr-1 h-3.5 w-3.5" />Ligada</Badge>
                : <Badge variant="destructive" data-testid="encryption-off"><LockOpen className="mr-1 h-3.5 w-3.5" />Desligada</Badge>}
            </div>
            {data.enabled ? (
              <p className="text-muted-foreground">
                {nf.format(encrypted)} valores protegidos.
                {data.pendingPlaintext > 0 ? ` ${nf.format(data.pendingPlaintext)} valores antigos ainda estão em texto puro.` : " Nenhum valor em texto puro."}
              </p>
            ) : (
              <p className="text-muted-foreground">
                Sem a chave, esses campos e os arquivos ficam sem cifra e não dá para dizer ao cliente que eles têm uma chave a mais. Para ligar: criar a
                variável <code className="rounded bg-muted px-1">{data.keyVariable}</code> no Cloud Run deste ambiente
                (<code className="rounded bg-muted px-1">openssl rand -base64 32</code>), guardar a mesma chave no cofre de senhas e voltar aqui.
                Perder a chave é perder esses campos e os arquivos cifrados.
              </p>
            )}
            {data.enabled && data.pendingPlaintext > 0 && (
              <Button onClick={() => void encryptOld()} disabled={busy} data-testid="button-encrypt-legacy">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Criptografar os antigos
              </Button>
            )}

            {files && (
              <div className="space-y-2 border-t pt-3" data-testid="files-encryption">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">Criptografia dos arquivos:</span>
                  {files.encryptNewFiles
                    ? <Badge className="bg-green-600 hover:bg-green-600" data-testid="files-encryption-on"><FileLock2 className="mr-1 h-3.5 w-3.5" />Ligada</Badge>
                    : <Badge variant="destructive" data-testid="files-encryption-off"><LockOpen className="mr-1 h-3.5 w-3.5" />Desligada</Badge>}
                </div>
                <p className="text-muted-foreground">
                  Documentos enviados, PDFs assinados e imagens de assinatura vão para o armazenamento já cifrados, cada arquivo com a própria chave.
                  Quem ler o armazenamento direto vê só bytes embaralhados. A FillSign abre o arquivo só na hora de mostrar para quem tem acesso.
                </p>
                {files.switchedOff && (
                  <p className="text-amber-700 dark:text-amber-400">
                    Desligada por <code className="rounded bg-muted px-1">{files.switchVariable}=false</code>: os arquivos novos estão sendo gravados sem cifra
                    (os já cifrados continuam abrindo). Remover a variável liga de novo.
                  </p>
                )}
                {converted && (
                  <p data-testid="files-convert-progress">
                    {filesBusy === "convert" ? "Criptografando… " : ""}
                    {nf.format(converted.checked)} arquivos vistos, {nf.format(converted.converted)} criptografados agora
                    {converted.failed ? `, ${nf.format(converted.failed)} com erro (ver log da API)` : ""}.
                  </p>
                )}
                {scan && (
                  <p data-testid="files-scan-result">
                    {filesBusy === "scan" ? "Verificando… " : ""}
                    {nf.format(scan.checked)} arquivos: {nf.format(scan.encrypted)} cifrados
                    {scan.plaintext > 0 ? `, ${nf.format(scan.plaintext)} antigos sem cifra` : ", nenhum sem cifra"}
                    {scan.failed ? `, ${nf.format(scan.failed)} não deu para ler` : ""}.
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => void scanFiles()} disabled={!!filesBusy} data-testid="button-scan-files">
                    {filesBusy === "scan" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileLock2 className="mr-2 h-4 w-4" />}
                    Verificar arquivos
                  </Button>
                  {data.enabled && (!scan || scan.plaintext > 0) && (
                    <Button onClick={() => void encryptFiles()} disabled={!!filesBusy} data-testid="button-encrypt-files">
                      {filesBusy === "convert" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                      Criptografar arquivos antigos
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
